import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { HostingBackupCatalogCard } from './HostingBackupCatalogCard';
import { useEffect, useId, useRef, useState } from 'react';
import type {
  HostingPleskDomainView,
  HostingPleskSiteProfileInput,
  HostingPleskTechnicalView,
  HostingPleskTechnicalState,
} from '@tosho/contracts';
import {
  hostingPleskInventoryApi,
  hostingPleskInventoryError,
} from './hosting-plesk-inventory-api';
import { EMPTY_SITE_PROFILE, SITE_CMS, SITE_ENVIRONMENTS } from './hosting-site-profile';
import { HostingSiteTechnicalDetails } from './HostingSiteTechnicalDetails';
import { hostingPleskApi, HostingPleskApiError } from './hosting-plesk-api';
import { HostingPleskInventoryApiError } from './hosting-plesk-inventory-api';

/** Une fiche locale par vhost ; les lectures fournisseur restent explicites ou déléguées. */
export function HostingSiteProfileDialog({
  domain,
  csrfToken,
  onClose,
  onSaved,
  onReauthenticate,
}: {
  readonly domain: HostingPleskDomainView;
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly onReauthenticate?: (() => void) | undefined;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const initial = domain.profile ?? EMPTY_SITE_PROFILE;
  const [cms, setCms] = useState(initial.cms),
    [otherCms, setOtherCms] = useState(initial.otherCms ?? ''),
    [environment, setEnvironment] = useState(initial.environment),
    [createdOn, setCreatedOn] = useState(initial.createdOn ?? ''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [locked, setLocked] = useState(false);
  const baseline = useRef(initial),
    lastRevision = useRef(domain.revision),
    busyRef = useRef(false);
  const [canRead, setCanRead] = useState(domain.presentInLatest && domain.accessIsCurrent);
  const [readError, setReadError] = useState<{
    kind: 'TECHNICAL' | 'BACKUPS';
    phase: 'LOCAL' | 'PROVIDER';
    message: string;
    reauth: boolean;
  } | null>(null);
  const readErrorElement = useRef<HTMLDivElement>(null);
  useEffect(() => {
    readErrorElement.current?.scrollIntoView?.({ block: 'nearest' });
  }, [readError]);
  const [technical, setTechnical] = useState<HostingPleskTechnicalView | null>(null),
    [loadingTechnical, setLoadingTechnical] = useState(true),
    [reading, setReading] = useState(false);
  const profile: HostingPleskSiteProfileInput = {
    cms,
    otherCms: cms === 'OTHER' ? otherCms.trim() : null,
    environment,
    createdOn: createdOn || null,
  };
  const unchanged =
    profile.cms === initial.cms &&
    profile.otherCms === initial.otherCms &&
    profile.environment === initial.environment &&
    profile.createdOn === initial.createdOn;
  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  function accept(result: HostingPleskTechnicalState): void {
    if (result.revision < lastRevision.current) return;
    lastRevision.current = result.revision;
    setTechnical(result.technical);
    setCanRead(result.canRead ?? (domain.presentInLatest && domain.accessIsCurrent));
    // Les observations peuvent changer seules. Ne jamais adopter un nouveau CAS
    // pour écraser une qualification modifiée par quelqu’un d’autre.
    if (
      result.profile
        ? !sameProfile(result.profile, baseline.current)
        : result.revision !== domain.revision
    ) {
      setLocked(true);
      setError(
        'La qualification du site a changé. Vos saisies sont conservées ; fermez et actualisez avant de les enregistrer. Les relevés Plesk restent consultables.',
      );
    }
  }
  useEffect(() => {
    let active = true;
    const load = async (): Promise<void> => {
      if (busyRef.current) return;
      try {
        const result = await hostingPleskInventoryApi.readTechnical(domain.id);
        if (active && !busyRef.current) {
          accept(result);
          setReadError((prior) => (prior?.phase === 'LOCAL' ? null : prior));
        }
      } catch {
        if (active)
          setReadError({
            kind: 'TECHNICAL',
            phase: 'LOCAL',
            message:
              'Les relevés enregistrés n’ont pas pu être rechargés. Vous pouvez réessayer la lecture ci-dessous.',
            reauth: false,
          });
      } finally {
        if (active) setLoadingTechnical(false);
      }
    };
    void load();
    // Cette relève locale n’utilise aucune clé et ne contacte jamais Plesk.
    const timer = window.setInterval(() => {
      void load();
    }, 15_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
    // Le contexte initial est celui de ce montage ; les saisies ne relancent pas la relève.
  }, [domain.id]);
  async function fresh(): Promise<HostingPleskTechnicalState> {
    let result = await hostingPleskInventoryApi.readTechnical(domain.id);
    if (result.revision < lastRevision.current)
      result = await hostingPleskInventoryApi.readTechnical(domain.id);
    if (result.revision < lastRevision.current)
      throw new HostingPleskInventoryApiError('PLESK_INVENTORY_CONFLICT');
    accept(result);
    return result;
  }
  async function save(): Promise<void> {
    if (busy || locked || unchanged) return;
    setBusy(true);
    busyRef.current = true;
    setError(null);
    try {
      const current = await fresh();
      if (
        current.profile
          ? !sameProfile(current.profile, baseline.current)
          : current.revision !== domain.revision
      )
        return;
      await hostingPleskInventoryApi.describeSite(domain.id, current.revision, profile, csrfToken);
      onSaved();
    } catch (failure) {
      setError(hostingPleskInventoryError(failure));
      setLocked(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }
  async function readProvider(): Promise<void> {
    if (busy || reading || !canRead) return;
    setReading(true);
    setBusy(true);
    busyRef.current = true;
    setReadError(null);
    try {
      const current = await fresh();
      if (current.canRead === false)
        throw new HostingPleskInventoryApiError('PLESK_INVENTORY_UNAVAILABLE');
      const access = await hostingPleskApi.read(domain.serverId);
      if (!access.access || access.access.status !== 'ACTIVE')
        throw new HostingPleskInventoryApiError('PLESK_INVENTORY_UNAVAILABLE');
      const result = await hostingPleskInventoryApi.observeTechnical(
        domain.id,
        current.revision,
        access.access.revision,
        csrfToken,
      );
      lastRevision.current = result.revision;
      setTechnical(result.technical);
    } catch (failure) {
      const normalized =
        failure instanceof HostingPleskApiError
          ? new HostingPleskInventoryApiError(failure.code)
          : failure;
      setReadError({
        kind: 'TECHNICAL',
        phase: 'PROVIDER',
        message: hostingPleskInventoryError(normalized),
        reauth:
          normalized instanceof HostingPleskInventoryApiError &&
          normalized.code === 'RECENT_AUTHENTICATION_REQUIRED',
      });
    } finally {
      setReading(false);
      busyRef.current = false;
      setBusy(false);
    }
  }
  function readFailure(): React.JSX.Element {
    return (
      <div
        ref={readErrorElement}
        className="hosting-site-editor__read-error tosho-feedback is-error"
        role="alert"
      >
        <p>{readError?.message}</p>
        {readError?.reauth && onReauthenticate ? (
          <button
            type="button"
            onClick={() => {
              onClose();
              onReauthenticate();
            }}
          >
            Se reconnecter
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor hosting-site-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          disabled={busy}
          onClick={onClose}
        />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <header className="hosting-site-editor__heading tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Sites observés</p>
            <h2 id={title}>Fiche du vhost</h2>
            <p>
              <strong>{domain.asciiName}</strong>
              <br />
              {domain.serverLabel}
            </p>
          </div>
        </header>
        <p className="hosting-site-editor__note">
          Ces informations sont renseignées dans Tosho et conservées lors des lectures Plesk.
        </p>
        {!canRead ? (
          <p role="status" className="hosting-site-editor__note">
            Fiche historique : une nouvelle lecture Plesk est nécessaire pour confirmer la présence
            du site.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        ) : null}
        <fieldset disabled={busy || locked}>
          <div className="hosting-servers-editor__technical">
            <label>
              CMS
              <select
                value={cms}
                onChange={(event) =>
                  setCms(event.target.value as HostingPleskSiteProfileInput['cms'])
                }
              >
                {Object.entries(SITE_CMS).map(([value, item]) => (
                  <option key={value} value={value}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Environnement
              <select
                value={environment}
                onChange={(event) =>
                  setEnvironment(event.target.value as HostingPleskSiteProfileInput['environment'])
                }
              >
                {Object.entries(SITE_ENVIRONMENTS).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {SITE_CMS[cms].logo ? (
            <img
              className="hosting-site-editor__cms-logo"
              src={SITE_CMS[cms].logo}
              alt={SITE_CMS[cms].name}
            />
          ) : null}
          {cms === 'OTHER' ? (
            <label>
              Nom du CMS
              <input
                value={otherCms}
                maxLength={64}
                required
                onChange={(event) => setOtherCms(event.target.value)}
              />
            </label>
          ) : null}
          <label>
            Date de création du vhost <span>(Plesk par défaut, modifiable)</span>
            <input
              type="date"
              min="1000-01-01"
              max="9999-12-31"
              value={createdOn || technical?.createdOn || domain.technicalSummary?.createdOn || ''}
              onChange={(event) => setCreatedOn(event.target.value)}
            />
          </label>
          <small>
            À déterminer signifie que le CMS n’est pas encore qualifié. « from scratch » indique un
            site développé sans CMS.
          </small>
        </fieldset>
        {readError?.kind === 'TECHNICAL' ? readFailure() : null}
        <HostingSiteTechnicalDetails
          value={technical}
          busy={reading}
          disabled={loadingTechnical || busy || !canRead}
          onRead={() => void readProvider()}
        />
        <HostingBackupCatalogCard serverId={domain.serverId} siteId={domain.id} />
        <footer>
          <button type="button" disabled={busy} onClick={onClose}>
            {locked ? 'Fermer et actualiser' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="hosting-servers__primary"
            disabled={busy || locked || unchanged || (cms === 'OTHER' && !otherCms.trim())}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}

function sameProfile(a: HostingPleskSiteProfileInput, b: HostingPleskSiteProfileInput): boolean {
  return (
    a.cms === b.cms &&
    a.otherCms === b.otherCms &&
    a.environment === b.environment &&
    a.createdOn === b.createdOn
  );
}
